import React, { useState, useEffect } from "react";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useTranslation } from 'react-i18next';

export default function CheckoutForm({ amount, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();

  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    );

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent.status) {
        case "succeeded":
          setMessage("Payment succeeded!");
          break;
        case "processing":
          setMessage("Your payment is processing.");
          break;
        case "requires_payment_method":
          setMessage("Your payment was not successful, please try again.");
          break;
        default:
          setMessage("Something went wrong.");
          break;
      }
    });
  }, [stripe]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // We don't need a return_url because we handle success in code, 
        // but Stripe might require it for some payment methods.
        // For card payments, we can often just wait for the promise.
        // However, with redirect-based methods, this is needed.
        return_url: window.location.href, 
      },
      redirect: "if_required", // Important: prevents redirect if not needed
    });

    if (error) {
      if (error.type === "card_error" || error.type === "validation_error") {
        setMessage(error.message);
      } else {
        setMessage("An unexpected error occurred.");
      }
      setIsLoading(false);
    } else if (paymentIntent && paymentIntent.status === "succeeded") {
      // Payment successful!
      setMessage("Payment success!");
      onSuccess(paymentIntent);
      setIsLoading(false);
    } else {
        setIsLoading(false);
    }
  };

  const paymentElementOptions = {
    layout: "tabs"
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit} className="w-full">
      <div className="mb-6">
         <PaymentElement id="payment-element" options={paymentElementOptions} />
      </div>
      
      {message && <div id="payment-message" className="text-red-500 text-sm mb-4 font-semibold text-center">{message}</div>}

      <div className="flex gap-3">
        <button
            type="button"
            onClick={onCancel}
            disabled={isLoading || !stripe || !elements}
            className="flex-1 py-3 rounded-xl border-2 border-neutral-200 dark:border-neutral-700 font-bold text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-all duration-300"
        >
            {t('bookingpage.back', 'Back')}
        </button>
        <button 
            disabled={isLoading || !stripe || !elements} 
            id="submit"
            className="flex-[2] py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
            <span id="button-text">
            {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
                <><span>💳</span> {t('bookingpage.payNow', 'Pay Now')} ${amount}</>
            )}
            </span>
        </button>
      </div>
    </form>
  );
}
